import { ErrorMessage } from '@/utils/errors';
import { sleep } from '@/utils/sleep';
import { waitForTransactionReceipt } from '@/utils/wait-transaction';
import { MiniKit } from '@worldcoin/minikit-js';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { toastApp } from '../toast-message/sonner';

const abi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_target',
                type: 'address',
            },
            {
                internalType: 'bytes',
                name: '_callData',
                type: 'bytes',
            },
            {
                internalType: 'bytes32',
                name: '_nonce',
                type: 'bytes32',
            },
            {
                internalType: 'bytes',
                name: '_signature',
                type: 'bytes',
            },
        ],
        name: 'claim',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const abiEmail = [
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: '_nonce',
                type: 'bytes32',
            },
            {
                internalType: 'bytes',
                name: '_signature',
                type: 'bytes',
            },
        ],
        name: 'claim',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

export function useClaimCampaign() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const claim = useCallback(
        async (params: {
            address: string;
            callData: string;
            nonce: string;
            signal: string;
            contractClaim?: string;
        }) => {
            try {
                if (!MiniKit.isInstalled()) {
                    throw new Error('Minikit not install');
                }
                const contract =
                    params.contractClaim ??
                    import.meta.env.VITE_CONTRACT_CAMPAIGN;
                setIsLoading(true);
                const payload = {
                    address: contract,
                    abi: abi,
                    functionName: 'claim',
                    args: [
                        params.address,
                        params.callData,
                        params.nonce,
                        params.signal,
                    ],
                };
                const { finalPayload, commandPayload } =
                    await MiniKit.commandsAsync.sendTransaction({
                        transaction: [payload],
                    });
                console.log({ finalPayload, commandPayload });

                if (finalPayload.status === 'error') {
                    const msg = ErrorMessage(finalPayload.error_code);
                    throw new Error(msg);
                }
                const txHash = await waitForTransactionReceipt(
                    finalPayload.transaction_id
                );

                await sleep(1000);
                toastApp.success(t('Claim Successfully'));
                return txHash;
            } catch (error: any) {
                const msg =
                    error?.message || 'Something went wrong, please try again';

                toastApp.error(msg);
            } finally {
                setIsLoading(false);
            }
        },
        []
    );
    return { claim, isLoading };
}

export function useClaimRewardEmail() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const claim = useCallback(
        async (params: { amount: number; nonce: string; signal: string }) => {
            try {
                if (!MiniKit.isInstalled()) {
                    throw new Error('Minikit not install');
                }
                const contract = import.meta.env.VITE_CONTRACT_CAMPAIGN_EMAIL;
                setIsLoading(true);
                const payload = {
                    address: contract,
                    abi: abiEmail,
                    functionName: 'claim',
                    args: [params.amount, params.nonce, params.signal],
                };

                console.debug('payload', payload);
                const { finalPayload, commandPayload } =
                    await MiniKit.commandsAsync.sendTransaction({
                        transaction: [payload],
                    });
                console.log({ finalPayload, commandPayload });

                if (finalPayload.status === 'error') {
                    const msg = ErrorMessage(finalPayload.error_code);
                    throw new Error(msg);
                }
                const txHash = await waitForTransactionReceipt(
                    finalPayload.transaction_id
                );
                await sleep(1000);
                toastApp.success(t('Claim Successfully'));
                return txHash;
            } catch (error: any) {
                const msg =
                    error?.message || 'Something went wrong, please try again';

                toastApp.error(msg);
            } finally {
                setIsLoading(false);
            }
        },
        []
    );
    return { claim, isLoading };
}
